import { FC, useCallback } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';

import { SafeFormattedMessageWithoutSpread } from '../../../formatters';
import { delay } from '../../../helpers';
import { useIsSuperAdmin } from '../../../hooks';
import { BaseLoader } from '../../../molecules/BaseLoader';
import ActionRow from '../../ActionRow/ActionRow';
import Button from '../../button/Button';
import Didactic from '../../Didactic/Didactic';
import FormGroup from '../../Form/FormGroup';
import Heading from '../../Heading/Heading';
import { Modal, MODAL_SIZES } from '../../modal';
import { useQrCodeImportModal } from '../QrCodeImportModal.hooks';

import messages from './messages';

interface QrCodeImportProps {
  onUploadFinishClick: ({ sessionId }: { sessionId: string }) => void;
  isSubmitLoading: boolean;
  id?: string;
  sellerId: string;
}

export const QrCodeImportModalContent: FC<QrCodeImportProps> = ({
  onUploadFinishClick,
  isSubmitLoading,
  id,
  sellerId,
}) => {
  const isSuperAdmin = useIsSuperAdmin();

  const { uploadQrCode, isLoading, uploadUrl, sessionId } =
    useQrCodeImportModal({
      id,
      sellerId,
    });

  const handleUploadFinishClick = useCallback(async () => {
    if (sessionId) {
      await delay(500);

      onUploadFinishClick({
        sessionId,
      });
    }
  }, [onUploadFinishClick, sessionId]);

  if (!uploadQrCode || isLoading || !sessionId) {
    return <BaseLoader />;
  }

  return (
    <Modal
      isOpen
      size={MODAL_SIZES.SMALL}
      onClose={async () => {
        onUploadFinishClick({
          sessionId,
        });
      }}
      shouldCloseOnDimmerClick={false}
    >
      <Heading level={3}>
        <SafeFormattedMessageWithoutSpread message={messages.title} />
      </Heading>
      <Didactic>
        <SafeFormattedMessageWithoutSpread message={messages.didactic} />
      </Didactic>

      <FormGroup>
        <img alt="qrcode" src={`data:${uploadQrCode}`} />
      </FormGroup>

      {isSuperAdmin && uploadUrl && (
        <FormGroup>
          <a href={uploadUrl} target="_blank" rel="noreferrer">
            {uploadUrl}
          </a>
        </FormGroup>
      )}

      <ActionRow>
        <Button
          success
          onClick={handleUploadFinishClick}
          type={BUTTON_TYPES.BUTTON}
          isLoading={isSubmitLoading}
          dataTestId="uploadFinishButton"
        >
          <SafeFormattedMessageWithoutSpread message={messages.uploadFinish} />
        </Button>
      </ActionRow>
    </Modal>
  );
};
